export default {
  props: {
    substeps: {
      type: Array,
      required: true,
    },
    currentUser: {
      type: Object,
      required: true,
      validator: (value) => !!value.first_name && !!value.id,
    },
    currentUserOrganization: {
      type: Object,
      default: () => ({}),
    },
    completionScreen: {
      type: Object,
      default: () => ({}),
    },
    organization: {
      type: Object,
      required: true,
      validator: (value) => !!value.id,
    },
    experiments: {
      type: Object,
      default: () => ({}),
    },
    currentSubstep: {
      type: Object,
      validator: (value) => !!value.id,
      default: () => ({}),
    },
    nextSubstep: {
      type: Object,
      default: null,
      validator: (value) => !value || !!value.name,
    },
    wizardFinished: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    substepData: null,
    updatedCurrentSubstep: null,
    loading: true,
    dynamicContinueTooltip: null,
    completingSubstep: false,
  }),
  computed: {
    allSubsteps() {
      return this.substeps;
    },
    currentSubstepComponent() {
      const componentsMap = this.substepComponentsMap;
      const componentName = this.currentSubstep.id;

      return componentsMap[componentName]?.component;
    },
    allowSkipToNextStep() {
      return this.substepData?.allow_skip_to_next_step || false;
    },
    isFirstSubstep() {
      if (!this.substeps?.length) {
        return true;
      }

      const currentSubstepIndex = this.allSubsteps.findIndex(
        (substep) => substep.status === 'current' || substep.status === 'completed',
      );

      return currentSubstepIndex === 0;
    },
    nextStepAllowed() {
      const substep = this.updatedCurrentSubstep || this.currentSubstep;

      return substep?.status === 'completed';
    },
    nextStepLinkText() {
      return this.nextSubstep ? `➜ Continue to ${this.nextSubstep?.name}` : '✓ Complete Onboarding';
    },
    nextStepText() {
      return this.nextSubstep ? 'Continue' : 'Complete onboarding';
    },
    showCompletionScreen() {
      return this.wizardFinished && this.completionScreen.template;
    },
    continueTooltip() {
      return this.dynamicContinueTooltip || this.substepData?.continue_tooltip;
    },
    showContinueTooltip() {
      return !this.nextStepAllowed && this.continueTooltip;
    },
    overallProgressPercentage() {
      if (!this.allSubsteps.length) {
        return 0;
      }
      const finished = this.allSubsteps.filter((s) => s.status === 'finished').length;

      return (finished / this.allSubsteps.length) * 100;
    },
  },
  watch: {
    currentSubstep() {
      this.dynamicContinueTooltip = null;
    },
  },
  mounted() {
    if (!this.isFirstSubstep) {
      const state = history.state || {};

      if (!state.__growthWizardBackTrap) {
        history.pushState({ ...state, __growthWizardBackTrap: true }, '', window.location.href);
      }

      window.addEventListener('popstate', this.handlePopState);
    }
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handlePopState);
  },
  methods: {
    getSubstepProps() {
      const componentName = this.currentSubstep.id;
      const config = this.substepComponentsMap[componentName];

      return config ? config.getProps(this) : {};
    },
    getSubstepEvents() {
      // Common events for all substeps
      return {
        completeSubstep: this.completeSubstep,
        incompleteSubstep: this.incompleteSubstep,
        reloadSubstep: this.reloadSubstep,
        userClickedContinue: this.handleFinishedSubstep,
        finishSubstep: this.finishSubstep,
        skipSubstep: this.skipToNextStep,
        updateTooltip: this.updateContinueTooltip,
      };
    },
    async getSubstepData() {
      if (this.wizardFinished) {
        return;
      }

      if (!this.substepData) {
        this.loading = true;
      }

      try {
        const response = await this.resourceAPI.substep(
          { organizationId: this.organization.id },
          { id: this.currentSubstep.id },
          { abortReload: true },
        );

        if (response.success) {
          this.substepData = response.data;
        } else if (response.errors) {
          this.$appMessage('Unexpected error. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      } finally {
        this.loading = false;
      }
    },
    async continueToNextStep(event) {
      if (!this.nextStepAllowed) {
        event?.preventDefault();

        return false;
      }

      if (this.substepsWithFinishedHandlers.includes(this.currentSubstep.id)) {
        this.$emit('userClickedContinue');

        return;
      }

      await this.updateSubstep('finished');
    },
    async skipToNextStep() {
      await this.updateSubstep('finished', { skipped: true });
    },
    handleFinishedSubstep() { },
    async updateSubstep(status, options = {}) {
      try {
        const response = await this.resourceAPI.updateSubstep(
          { organizationId: this.organization.id },
          { substep: this.currentSubstep.id, status, ...options },
          { abortReload: true },
        );

        if (response.success) {
          const returnedSubstep = response.current_substep || response.currentSubstep;

          if (returnedSubstep) {
            this.updatedCurrentSubstep = {
              ...this.currentSubstep,
              ...returnedSubstep,
              id: returnedSubstep.id || this.currentSubstep.id,
              status: returnedSubstep.status || status,
            };
          } else {
            this.updatedCurrentSubstep = { ...this.currentSubstep, status };
          }

          if (status === 'finished') {
            window.location.reload();
          }

          return true;
        }

        if (response.errors) {
          this.$appMessage('Unexpected error. Please try again.');
        }

        return false;
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }

        throw error;
      }
    },
    async completeSubstep() {
      const substep = this.updatedCurrentSubstep || this.currentSubstep;

      if (substep.status === 'completed' || this.completingSubstep) {
        return;
      }

      this.completingSubstep = true;

      try {
        const success = await this.updateSubstep('completed');

        if (!success) {
          return;
        }

        this.substepCompleted = true;

        if (this.updatedCurrentSubstep?.status !== 'completed') {
          this.updatedCurrentSubstep = { ...substep, status: 'completed' };
        }
      } finally {
        this.completingSubstep = false;
      }
    },
    async finishSubstep() {
      await this.updateSubstep('finished');
    },
    async incompleteSubstep() {
      await this.updateSubstep('todo');
    },
    reloadSubstep() {
      return this.getSubstepData();
    },
    async returnToPreviousStep(event) {
      await this.previousSubstep();
    },
    async previousSubstep() {
      try {
        const response = await this.resourceAPI.previousSubstep({ organizationId: this.organization.id });

        if (response.success) {
          this.updatedCurrentSubstep = response.current_substep;

          window.location.reload();
        } else if (response.errors) {
          this.$appMessage('Unexpected error. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      }
    },
    exploreOnMyOwn() {
      const sidebar = document.querySelector('[x-data^="sidebar"]')?.__x;

      if (!sidebar) {
        return;
      }

      if (sidebar.state.collapsed) {
        sidebar.toggleCollapse(false);
      }

      if (!sidebar.state.menuItemsVisible) {
        sidebar.toggleMenuItems();
      }
    },
    async handlePopState(event) {
      if (this._handlingPopState) {
        return;
      }

      this._handlingPopState = true;

      try {
        await this.returnToPreviousStep();
      } catch (error) {
        this.$appMessage('Failed to go back. Please try again.');
        // Restore forward state since navigation failed
        history.pushState(null, '', window.location.href);
      } finally {
        this._handlingPopState = false;
      }
    },
    updateContinueTooltip(tooltip) {
      this.dynamicContinueTooltip = tooltip;
    },
  },
};
